// FILE: src/components/admin/EmailAnalyticsScreen.js
//
// Admin → Email Automation (read-only observability). Renders lifecycle-email
// analytics from the admin-only RPCs (migration 068): job status by journey,
// delivery/retry/suppression metrics, and recent failures. No sends, no journey
// controls — purely a dashboard. Server enforces admin access; a non-admin
// simply gets empty data.

import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchEmailMetrics,
  fetchEmailRecentFailures,
} from '../../services/email/emailAnalyticsService';

const JOURNEYS = [
  { key: '', label: 'All journeys' },
  { key: 'welcome_v1', label: 'Welcome' },
  { key: 'inactive_24h_v1', label: 'Inactive 24h' },
  { key: 'inactive_4d_v1', label: 'Inactive 4d' },
  { key: 'first_practice_complete_v1', label: 'First practice' },
  { key: 'simulation_complete_v1', label: 'Simulation complete' },
];
const CERTS = [
  { key: '', label: 'All certs' },
  { key: 'pmp', label: 'PMP®' },
  { key: 'cpmai', label: 'CPMAI™' },
  { key: 'pmi-acp', label: 'PMI-ACP®' },
  { key: 'pmi-pmocp', label: 'PMI-PMOCP™' },
];

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const S = {
  root: { padding: '20px', maxWidth: 1100, margin: '0 auto', fontFamily: '-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif', color: '#1a2233' },
  bar: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0 18px' },
  back: { background: 'transparent', border: '1px solid #ccd4e6', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#1a2233' },
  input: { padding: '7px 9px', border: '1px solid #ccd4e6', borderRadius: 8, fontSize: 14 },
  cards: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 18 },
  card: { background: '#fff', border: '1px solid #e6eaf2', borderRadius: 12, padding: '14px 16px' },
  cardH: { fontSize: 12, textTransform: 'uppercase', letterSpacing: '.5px', color: '#6b7280', fontWeight: 800, marginBottom: 8 },
  kv: { display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '2px 0' },
  num: { fontVariantNumeric: 'tabular-nums', fontWeight: 700 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13, background: '#fff', border: '1px solid #e6eaf2', borderRadius: 12, overflow: 'hidden' },
  th: { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', letterSpacing: '.5px', color: '#6b7280', fontWeight: 800, padding: '10px 12px', borderBottom: '1px solid #eef1f7' },
  td: { padding: '9px 12px', borderBottom: '1px solid #f1f4fa', verticalAlign: 'top' },
  h2: { fontSize: 15, margin: '18px 0 8px', color: '#1a2233' },
};

function Card({ title, obj }) {
  return (
    <div style={S.card}>
      <div style={S.cardH}>{title}</div>
      {Object.entries(obj || {}).map(([k, v]) => (
        <div key={k} style={S.kv}><span>{k.replace(/_/g, ' ')}</span><span style={S.num}>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span></div>
      ))}
    </div>
  );
}

export default function EmailAnalyticsScreen({ onBack = () => {} }) {
  const [filters, setFilters] = useState({ from: isoDaysAgo(30), to: isoDaysAgo(0), journey: '', examId: '' });
  const [metrics, setMetrics] = useState(null);
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const opts = {
      from: filters.from, to: filters.to,
      journey: filters.journey || undefined, examId: filters.examId || undefined,
    };
    const [m, f] = await Promise.all([
      fetchEmailMetrics(opts),
      fetchEmailRecentFailures({ journey: filters.journey || undefined, limit: 50 }),
    ]);
    setMetrics(m);
    setFailures(Array.isArray(f) ? f : []);
    setLoading(false);
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (e) => setFilters((p) => ({ ...p, [k]: e.target.value }));
  const byJourney = (metrics && metrics.by_journey) || [];

  return (
    <div style={S.root} data-testid="email-analytics-screen">
      <div style={S.bar}>
        <button style={S.back} onClick={onBack} data-testid="email-analytics-back">← Back</button>
        <h1 style={{ fontSize: 20, margin: 0 }}>Email Automation</h1>
        <span style={{ color: '#6b7280', fontSize: 13 }}>read-only observability</span>
      </div>

      <div style={S.bar}>
        <label>From <input type="date" style={S.input} value={filters.from} onChange={set('from')} /></label>
        <label>To <input type="date" style={S.input} value={filters.to} onChange={set('to')} /></label>
        <select style={S.input} value={filters.journey} onChange={set('journey')} aria-label="journey">
          {JOURNEYS.map((j) => <option key={j.key} value={j.key}>{j.label}</option>)}
        </select>
        <select style={S.input} value={filters.examId} onChange={set('examId')} aria-label="cert">
          {CERTS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <button style={S.back} onClick={load} data-testid="email-analytics-refresh">{loading ? 'Loading…' : 'Refresh'}</button>
      </div>

      {!metrics ? (
        <p style={{ color: '#6b7280' }}>{loading ? 'Loading…' : 'No data available.'}</p>
      ) : (
        <>
          <div style={S.cards}>
            <Card title="Jobs by status" obj={metrics.status_counts} />
            <Card title="Delivery events" obj={metrics.delivery} />
            <Card title="Retry" obj={metrics.retry} />
            <Card title="Suppression" obj={metrics.suppression} />
          </div>

          <h2 style={S.h2}>By journey</h2>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Journey</th><th style={S.th}>Sent</th><th style={S.th}>Failed</th><th style={S.th}>Pending</th><th style={S.th}>Suppressed</th><th style={S.th}>Total</th></tr></thead>
            <tbody>
              {byJourney.length === 0 ? (
                <tr><td style={S.td} colSpan={6}>No jobs in range.</td></tr>
              ) : byJourney.map((r) => (
                <tr key={r.template_key}>
                  <td style={S.td}>{r.template_key}</td>
                  <td style={S.td}>{r.sent}</td><td style={S.td}>{r.failed}</td>
                  <td style={S.td}>{r.pending}</td><td style={S.td}>{r.suppressed}</td><td style={S.td}>{r.total}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h2 style={S.h2}>Recent failures</h2>
          <table style={S.table}>
            <thead><tr><th style={S.th}>Journey</th><th style={S.th}>Status</th><th style={S.th}>Attempts</th><th style={S.th}>Reason</th><th style={S.th}>Last attempt</th></tr></thead>
            <tbody>
              {failures.length === 0 ? (
                <tr><td style={S.td} colSpan={5}>No recent failures.</td></tr>
              ) : failures.map((r) => (
                <tr key={r.id}>
                  <td style={S.td}>{r.template_key}</td>
                  <td style={S.td}>{r.status}</td>
                  <td style={S.td}>{r.attempt_count}</td>
                  <td style={S.td}>{r.last_error}</td>
                  <td style={S.td}>{r.last_attempt_at ? new Date(r.last_attempt_at).toLocaleString() : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
